/**
 * SearchInput Component
 *
 * Input component with real-time validation for word searches.
 * Supports anagram, pattern, and verify search types.
 *
 * Features:
 * - Real-time validation (1-15 characters, A-Z, *)
 * - Search type selector
 * - Loading state on submit button
 * - Touch-friendly (44x44px minimum targets)
 * - Anagram mode: max 2 jokers
 *
 * @validates Requirements 1.6, 1.7, 1.8, 10.3, 11.2
 */

import { useState, useCallback, useEffect, useMemo, type ChangeEvent, type FormEvent } from 'react';
import { SearchType, VALIDATION } from '@spellion/shared';

// ============================================================================
// Types
// ============================================================================

export interface SearchInputProps {
  /** Initial search type */
  initialSearchType?: SearchType;
  /** Initial query value */
  initialQuery?: string;
  /** Called when a valid search is submitted */
  onSearch: (query: string, searchType: SearchType) => void;
  /** Whether a search is currently in progress */
  isLoading?: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Disable the input */
  disabled?: boolean;
}

interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

// ============================================================================
// Validation Utilities
// ============================================================================

/**
 * Validates search input based on search type
 *
 * Validation rules:
 * - 1-15 characters length
 * - Only A-Z (case-insensitive) and * (joker) allowed
 * - For anagram: max 2 jokers
 *
 * @validates Requirements 1.6, 1.7, 1.8
 */
function validateInput(value: string, searchType: SearchType): ValidationResult {
  // Normalize to uppercase for validation
  const normalized = value.toUpperCase().trim();

  // Empty input
  if (normalized.length === 0) {
    return {
      isValid: false,
      error: 'Au moins une lettre est requise',
    };
  }

  // Check length (1-15 characters)
  if (normalized.length > VALIDATION.MAX_WORD_LENGTH) {
    return {
      isValid: false,
      error: `Maximum ${VALIDATION.MAX_WORD_LENGTH} caractères autorisés`,
    };
  }

  // Check valid characters (A-Z and * only)
  const validCharsRegex = /^[A-Z*]+$/;
  if (!validCharsRegex.test(normalized)) {
    return {
      isValid: false,
      error: 'Seules les lettres A-Z et * (joker) sont autorisées',
    };
  }

  // For anagram search, check max 2 jokers
  if (searchType === SearchType.ANAGRAM) {
    const jokerCount = (normalized.match(/\*/g) || []).length;
    if (jokerCount > VALIDATION.MAX_JOKERS) {
      return {
        isValid: false,
        error: `Maximum ${VALIDATION.MAX_JOKERS} jokers autorisés`,
      };
    }
  }

  // For verify, jokers are not allowed
  if (searchType === SearchType.VERIFY) {
    if (normalized.includes('*')) {
      return {
        isValid: false,
        error: 'Les jokers ne sont pas autorisés pour la vérification',
      };
    }
  }

  return {
    isValid: true,
    error: null,
  };
}

// ============================================================================
// Search Type Options
// ============================================================================

const SEARCH_TYPE_OPTIONS: Array<{
  value: SearchType;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    value: SearchType.ANAGRAM,
    label: 'Anagrammes',
    description: 'Trouve tous les mots formables avec ces lettres',
    icon: '🔄',
  },
  {
    value: SearchType.PATTERN,
    label: 'Pattern',
    description: 'Trouve les mots correspondant au motif (* = joker)',
    icon: '🎯',
  },
  {
    value: SearchType.VERIFY,
    label: 'Vérifier',
    description: 'Vérifie si un mot existe dans le dictionnaire',
    icon: '✓',
  },
];

// ============================================================================
// Component
// ============================================================================

export function SearchInput({
  initialSearchType = SearchType.ANAGRAM,
  initialQuery = '',
  onSearch,
  isLoading = false,
  placeholder,
  disabled = false,
}: SearchInputProps) {
  const [query, setQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState<SearchType>(initialSearchType);
  const [touched, setTouched] = useState(false);

  // Update query when initialQuery changes (for URL params)
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  // Update search type when initialSearchType changes (for URL params)
  useEffect(() => {
    if (initialSearchType) {
      setSearchType(initialSearchType);
    }
  }, [initialSearchType]);

  // Get dynamic placeholder based on search type
  const dynamicPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;

    switch (searchType) {
      case SearchType.ANAGRAM:
        return 'Ex: ABEILL* (lettres + jokers)';
      case SearchType.PATTERN:
        return 'Ex: B**LE (* = joker)';
      case SearchType.VERIFY:
        return 'Ex: BELLE';
      default:
        return 'Entrez votre recherche...';
    }
  }, [placeholder, searchType]);

  // Validate current input
  const validation = useMemo(
    () => validateInput(query, searchType),
    [query, searchType]
  );

  // Show error only if touched and invalid
  const showError = touched && !validation.isValid && query.length > 0;

  // Handle input change
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setQuery(value);
    if (!touched) setTouched(true);
  }, [touched]);

  // Handle search type change
  const handleSearchTypeChange = useCallback((newType: SearchType) => {
    setSearchType(newType);
    // Re-validate with new type
    setTouched(query.length > 0);
  }, [query.length]);

  // Handle form submission
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setTouched(true);

      if (validation.isValid && !isLoading && !disabled) {
        onSearch(query.toUpperCase().trim(), searchType);
      }
    },
    [query, searchType, validation.isValid, isLoading, disabled, onSearch]
  );

  // Reset touched state when query is cleared
  useEffect(() => {
    if (query.length === 0) {
      setTouched(false);
    }
  }, [query]);

  const isSubmitDisabled = disabled || isLoading || !validation.isValid || query.length === 0;

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-4">
      {/* Search Type Selector */}
      <div
        className="flex flex-wrap gap-2"
        role="radiogroup"
        aria-label="Type de recherche"
      >
        {SEARCH_TYPE_OPTIONS.map((option) => {
          // Different colors for each search type
          const getSelectedStyles = () => {
            switch (option.value) {
              case SearchType.ANAGRAM:
                return 'bg-primary-500 text-white shadow-md';
              case SearchType.PATTERN:
                return 'bg-secondary-500 text-white shadow-md';
              case SearchType.VERIFY:
                return 'bg-accent-500 text-white shadow-md';
              default:
                return 'bg-primary-500 text-white shadow-md';
            }
          };

          const getHoverStyles = () => {
            switch (option.value) {
              case SearchType.ANAGRAM:
                return 'hover:border-primary-300 hover:bg-primary-50';
              case SearchType.PATTERN:
                return 'hover:border-secondary-300 hover:bg-secondary-50';
              case SearchType.VERIFY:
                return 'hover:border-accent-300 hover:bg-accent-50';
              default:
                return 'hover:border-primary-300 hover:bg-primary-50';
            }
          };

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={searchType === option.value}
              onClick={() => handleSearchTypeChange(option.value)}
              disabled={disabled}
              className={`
                flex items-center gap-2 px-4 py-2
                min-h-touch min-w-touch
                rounded-lg font-medium
                focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500
                feedback
                ${
                  searchType === option.value
                    ? getSelectedStyles()
                    : `bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-600 ${getHoverStyles()}`
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              title={option.description}
            >
              <span aria-hidden="true">{option.icon}</span>
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>

      {/* Search Input Field */}
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder={dynamicPlaceholder}
            disabled={disabled || isLoading}
            aria-label="Recherche de mots"
            aria-invalid={showError}
            aria-describedby={showError ? 'search-error' : undefined}
            maxLength={VALIDATION.MAX_WORD_LENGTH}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            className={`
              input pr-28 text-lg font-mono tracking-wider
              ${
                showError
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : validation.isValid && query.length > 0
                  ? 'border-success-500 focus:border-success-500 focus:ring-success-500'
                  : ''
              }
            `}
          />

          {/* Character Counter - using slate-500 for 4.5:1 contrast compliance */}
          <div
            className={`
              absolute right-20 top-1/2 -translate-y-1/2
              text-sm font-medium
              ${
                query.length > VALIDATION.MAX_WORD_LENGTH - 3
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-slate-500 dark:text-slate-400'
              }
            `}
            aria-live="polite"
            aria-label={`${query.length} caractères sur ${VALIDATION.MAX_WORD_LENGTH} maximum`}
          >
            {query.length}/{VALIDATION.MAX_WORD_LENGTH}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className={`
              absolute right-2 top-1/2 -translate-y-1/2
              min-h-touch min-w-touch
              flex items-center justify-center
              px-4 rounded-lg
              font-medium
              focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500
              feedback
              ${
                isSubmitDisabled
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }
            `}
            aria-label={isLoading ? 'Recherche en cours...' : 'Lancer la recherche'}
          >
            {isLoading ? (
              <LoadingSpinner className="w-5 h-5" />
            ) : (
              <SearchIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Error Message */}
        {showError && (
          <p
            id="search-error"
            role="alert"
            className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 animate-fade-in-up"
          >
            <ErrorIcon className="w-4 h-4 flex-shrink-0" />
            <span>{validation.error}</span>
          </p>
        )}

        {/* Help Text */}
        {!showError && (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {searchType === SearchType.ANAGRAM && (
              <>
                Utilisez <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-primary-600 dark:text-primary-400 font-mono">*</code> comme joker (max 2)
              </>
            )}
            {searchType === SearchType.PATTERN && (
              <>
                Utilisez <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-primary-600 dark:text-primary-400 font-mono">*</code> pour remplacer une lettre
              </>
            )}
            {searchType === SearchType.VERIFY && (
              <>Entrez un mot pour vérifier s'il existe dans le dictionnaire</>
            )}
          </p>
        )}
      </div>
    </form>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`${className} animate-spin`}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

export default SearchInput;
